import React, { useState } from 'react';

import Button from '~/components/core/Atomic/Buttons/Button';
import ButtonsContainer from '~/components/core/Atomic/Buttons/ButtonsContainer';
import Text from '~/components/core/TextComponents/Text';
import Checkbox from '~/components/ui/atoms/Checkbox';
import type { ConsentDecisionRequest, ConsentDetails } from '~/server_shared/openapi/data-contracts';

import { TEXTS } from './texts';

// The loopback hosts a native client's redirect URI may name (RFC 8252 sections 7.3 and 8.3), as a URL's hostname spells them.
const LOOPBACK_HOSTNAMES = ['localhost', '127.0.0.1', '[::1]'];

/** Whether `redirectHost`, a host with or without a port, is this computer. */
const isLoopbackHost = (redirectHost: string): boolean =>
  LOOPBACK_HOSTNAMES.includes(new URL(`http://${redirectHost}`).hostname);

/** The user's answer, which the page sends with the consent request it was opened with. */
export type ConsentAnswer = Omit<ConsentDecisionRequest, 'request'>;

interface ConsentFormProps {
  details: ConsentDetails;
  isDeciding: boolean;
  onDecide: (answer: ConsentAnswer) => void;
}

/**
 * The client with the host it returns to, the signed-in user, and the scopes asked for, each ticked. The API's `required` flag locks a scope on;
 * the user may untick the others.
 */
const ConsentForm: React.FC<ConsentFormProps> = ({ details, isDeciding, onDecide }) => {
  const [withheldScopes, setWithheldScopes] = useState<string[]>([]);

  const handleScopeChange = (scope: string, checked: boolean) => {
    setWithheldScopes((withheld) => (checked ? withheld.filter((item) => item !== scope) : [...withheld, scope]));
  };

  const handleApprove = () => {
    const approvedScopes = details.scopes
      .filter(({ scope }) => !withheldScopes.includes(scope))
      .map(({ scope }) => scope);
    onDecide({ approved: true, approved_scopes: approvedScopes });
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <Text variant={Text.VARIANTS.SM}>{TEXTS.CLIENT_ASKS(details.client_name, details.redirect_host)}</Text>
        <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {TEXTS.CLIENT_NAME_UNVERIFIED(details.redirect_host)}
        </Text>
        {isLoopbackHost(details.redirect_host) && (
          <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.NOTICE}>
            {TEXTS.LOOPBACK_REDIRECT}
          </Text>
        )}
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {TEXTS.SIGNED_IN_AS(details.user_email)}
        </Text>
      </div>
      <div className="flex flex-col gap-8">
        <Text variant={Text.VARIANTS.SM}>{TEXTS.SCOPES_TITLE}</Text>
        {details.scopes.map(({ scope, description, required }) => (
          <Checkbox
            key={scope}
            checked={!withheldScopes.includes(scope)}
            disabled={required}
            onChange={(_event, checked) => handleScopeChange(scope, checked)}
            label={
              <>
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                  {description}
                </Text>
                <Text
                  variant={Text.VARIANTS.XS}
                  weight={Text.WEIGHTS.REGULAR}
                  colorVariant={Text.COLOR_VARIANTS.SECONDARY}
                >
                  {required ? TEXTS.REQUIRED_SCOPE(scope) : scope}
                </Text>
              </>
            }
          />
        ))}
      </div>
      <div>
        <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {TEXTS.RETURNS_TO(details.redirect_host)}
        </Text>
        <ButtonsContainer className="gap-12">
          <Button
            variant="outlined"
            color="primary"
            disabled={isDeciding}
            onClick={() => onDecide({ approved: false })}
          >
            {TEXTS.DENY}
          </Button>
          <Button variant="contained" color="primary" disabled={isDeciding} onClick={handleApprove}>
            {TEXTS.APPROVE}
          </Button>
        </ButtonsContainer>
      </div>
    </>
  );
};

export default ConsentForm;
